"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    products: string[];
    assets: string[];
    pickTitle: string;
    inside: string;
    overlap: string;
    hardToSeeTilt: string;
    designTitle: string;
    whole: string;
    balance: string;
    byRoleAndWeight: string;
    switch1: string;
    switch2: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    products: ["米国株ファンド", "高配当ETF", "人気テーマ型", "話題のファンド"],
    assets: ["株式", "債券", "預金"],
    pickTitle: "商品ごとに選ぶ",
    inside: "中身が",
    overlap: "重なる？",
    hardToSeeTilt: "偏りに気づきにくい",
    designTitle: "全体を設計する",
    whole: "全体の",
    balance: "バランス",
    byRoleAndWeight: "役割と比率で構成する",
    switch1: "視点を",
    switch2: "切り替える",
    title: "商品選びと設計は別の問い",
    description:
      "株式や投資信託の特性を理解したうえで、自分が許容できる値動きに合う全体の構成を考えます。",
  },
  en: {
    products: ["US equity fund", "High-dividend ETF", "Popular theme fund", "The fund everyone mentions"],
    assets: ["Equities", "Bonds", "Deposits"],
    pickTitle: "Choosing product by product",
    inside: "Do the",
    overlap: "holdings overlap?",
    hardToSeeTilt: "The tilt is hard to notice",
    designTitle: "Designing the whole",
    whole: "Overall",
    balance: "balance",
    byRoleAndWeight: "Built from roles and weights",
    switch1: "Switch",
    switch2: "the lens",
    title: "Picking products and designing the whole are different questions",
    description:
      "Once you understand how equities and funds behave, work out an overall mix that matches the movement you can tolerate.",
  },
};

// ── Colors ─────────────────────────────────────────────────────────

const C = {
  sky: COLOR.sky,
  rose: COLOR.rose,
  emerald: COLOR.emerald,
  amber: COLOR.amber,
};

// ── SVG primitives ─────────────────────────────────────────────────

function ProductChip({
  x,
  y,
  w,
  h,
  color,
  label,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  label: string;
}) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={h / 2}
        fill={color}
        fillOpacity={0.15}
        stroke={color}
        strokeWidth="1"
      />
      <text
        x={x + w / 2}
        y={y + h / 2 + 4}
        textAnchor="middle"
        fontSize="10"
        fill={color}
        fontWeight="600"
      >
        {label}
      </text>
    </g>
  );
}

function StarBurst({ cx, cy, r, color }: { cx: number; cy: number; r: number; color: string }) {
  const points = 5;
  const inner = r * 0.5;
  const d =
    Array.from({ length: points * 2 }, (_, i) => {
      const angle = (Math.PI / points) * i - Math.PI / 2;
      const radius = i % 2 === 0 ? r : inner;
      const px = cx + radius * Math.cos(angle);
      const py = cy + radius * Math.sin(angle);
      return `${i === 0 ? "M" : "L"} ${px} ${py}`;
    }).join(" ") + " Z";
  return <path d={d} fill={color} fillOpacity={0.2} stroke={color} strokeWidth="0.8" />;
}

// ── Left side: choosing product by product ──────────────────────

function ProductPickingSection() {
  const t = STRINGS[useDocLocale()];
  const x0 = 16;
  const chipW = 120;
  const chipH = 28;
  const gap = 12;
  const products = [
    { label: t.products[0]!, color: C.sky },
    { label: t.products[1]!, color: C.sky },
    { label: t.products[2]!, color: C.amber },
    { label: t.products[3]!, color: C.amber },
  ];

  const startY = 40;
  const totalH = products.length * (chipH + gap) - gap;
  const bracketX = x0 + chipW + 16;

  return (
    <g>
      <text
        x={x0 + chipW / 2 + 10}
        y={20}
        textAnchor="middle"
        fontSize="12"
        fill="currentColor"
        fontWeight="700"
      >
        {t.pickTitle}
      </text>
      {products.map((p, i) => {
        const py = startY + i * (chipH + gap);
        return (
          <g key={p.label}>
            <StarBurst cx={x0 + 8} cy={py + chipH / 2} r={7} color={p.color} />
            <ProductChip x={x0 + 20} y={py} w={chipW} h={chipH} color={p.color} label={p.label} />
          </g>
        );
      })}

      {/* Overlap bracket */}
      <path
        d={`M ${bracketX} ${startY + 6} C ${bracketX + 12} ${startY + 6}, ${bracketX + 12} ${startY + chipH + gap + chipH - 6}, ${bracketX} ${startY + chipH + gap + chipH - 6}`}
        fill="none"
        stroke={C.rose}
        strokeWidth="1.2"
        strokeDasharray="3 3"
      />
      <text
        x={bracketX + 16}
        y={startY + (chipH + gap) / 2 + chipH / 2}
        fontSize="8.5"
        fill={C.rose}
        fontWeight="500"
      >
        {t.inside}
      </text>
      <text
        x={bracketX + 16}
        y={startY + (chipH + gap) / 2 + chipH / 2 + 12}
        fontSize="8.5"
        fill={C.rose}
        fontWeight="500"
      >
        {t.overlap}
      </text>

      {/* Bottom note */}
      <text
        x={x0 + chipW / 2 + 10}
        y={startY + totalH + 24}
        textAnchor="middle"
        fontSize="9"
        fill={C.rose}
        fontWeight="500"
      >
        {t.hardToSeeTilt}
      </text>
    </g>
  );
}

// ── Right side: designing the whole ─────────────────────────────

function PortfolioDesignSection() {
  const t = STRINGS[useDocLocale()];
  const x0 = 310;
  const cx = x0 + 80;
  const cy = 120;
  const outerR = 72;
  const innerR = 40;

  const slices = [
    { label: t.assets[0]!, pct: 50, color: C.sky },
    { label: t.assets[1]!, pct: 25, color: C.emerald },
    { label: t.assets[2]!, pct: 25, color: C.amber },
  ];

  function arcPath(startDeg: number, endDeg: number, oR: number, iR: number) {
    const toRad = (d: number) => ((d - 90) * Math.PI) / 180;
    const x1 = cx + oR * Math.cos(toRad(startDeg));
    const y1 = cy + oR * Math.sin(toRad(startDeg));
    const x2 = cx + oR * Math.cos(toRad(endDeg));
    const y2 = cy + oR * Math.sin(toRad(endDeg));
    const x3 = cx + iR * Math.cos(toRad(endDeg));
    const y3 = cy + iR * Math.sin(toRad(endDeg));
    const x4 = cx + iR * Math.cos(toRad(startDeg));
    const y4 = cy + iR * Math.sin(toRad(startDeg));
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${oR} ${oR} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${iR} ${iR} 0 ${large} 0 ${x4} ${y4} Z`;
  }

  let currentAngle = 0;
  const drawnSlices = slices.map((s) => {
    const start = currentAngle;
    const sweep = (s.pct / 100) * 360;
    currentAngle += sweep;
    const midAngle = ((start + sweep / 2 - 90) * Math.PI) / 180;
    const labelR = outerR + 18;
    return {
      ...s,
      path: arcPath(start, start + sweep, outerR, innerR),
      labelX: cx + labelR * Math.cos(midAngle),
      labelY: cy + labelR * Math.sin(midAngle),
      pctX: cx + (outerR + innerR) * 0.5 * Math.cos(midAngle),
      pctY: cy + (outerR + innerR) * 0.5 * Math.sin(midAngle),
    };
  });

  return (
    <g>
      <text
        x={cx}
        y={20}
        textAnchor="middle"
        fontSize="12"
        fill="currentColor"
        fontWeight="700"
      >
        {t.designTitle}
      </text>

      {drawnSlices.map((s) => (
        <g key={s.label}>
          <path
            d={s.path}
            fill={s.color}
            fillOpacity={0.18}
            stroke={s.color}
            strokeWidth="1.2"
          />
          <text
            x={s.labelX}
            y={s.labelY + 4}
            textAnchor="middle"
            fontSize="10"
            fill={s.color}
            fontWeight="600"
          >
            {s.label}
          </text>
          <text
            x={s.pctX}
            y={s.pctY + 4}
            textAnchor="middle"
            fontSize="10"
            fill={s.color}
            fontWeight="700"
          >
            {s.pct}%
          </text>
        </g>
      ))}

      {/* Center label */}
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        fontSize="10"
        fill="currentColor"
        fontWeight="600"
      >
        {t.whole}
      </text>
      <text
        x={cx}
        y={cy + 10}
        textAnchor="middle"
        fontSize="10"
        fill="currentColor"
        fontWeight="600"
      >
        {t.balance}
      </text>

      {/* Bottom note */}
      <text
        x={cx}
        y={cy + outerR + 36}
        textAnchor="middle"
        fontSize="9"
        fill={C.emerald}
        fontWeight="500"
      >
        {t.byRoleAndWeight}
      </text>
    </g>
  );
}

// ── Center arrow ───────────────────────────────────────────────────

function CenterArrow() {
  const t = STRINGS[useDocLocale()];
  const x = 220;
  const y = 120;
  const arrowColor = "#94a3b8";

  return (
    <g>
      <line
        x1={x}
        y1={y - 30}
        x2={x}
        y2={y + 30}
        stroke={arrowColor}
        strokeWidth="1"
        strokeDasharray="4 4"
      />
      <path
        d={`M ${x - 18} ${y - 3} L ${x + 18} ${y - 3} L ${x + 18} ${y - 8} L ${x + 28} ${y} L ${x + 18} ${y + 8} L ${x + 18} ${y + 3} L ${x - 18} ${y + 3} Z`}
        fill={arrowColor}
        fillOpacity={0.12}
        stroke={arrowColor}
        strokeWidth="0.8"
      />
      <text
        x={x + 5}
        y={y - 16}
        textAnchor="middle"
        fontSize="9"
        fill={arrowColor}
      >
        {t.switch1}
      </text>
      <text
        x={x + 5}
        y={y + 26}
        textAnchor="middle"
        fontSize="9"
        fill={arrowColor}
      >
        {t.switch2}
      </text>
    </g>
  );
}

// ── Main illustration ──────────────────────────────────────────────

function DesignVsPickIllustration() {
  const vw = 480;
  const vh = 240;
  return (
    <svg viewBox={`0 0 ${vw} ${vh}`} className="mx-auto w-full max-w-2xl">
      <ProductPickingSection />
      <CenterArrow />
      <PortfolioDesignSection />
    </svg>
  );
}

// ── Export ──────────────────────────────────────────────────────────

export function DocPortfolioDesignDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <DesignVsPickIllustration />
    </IllustrationCard>
  );
}
